import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {  CheckCircle2, Clock, AlertTriangle, Eye, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

const assignments = [
  { id: 1, title: 'Build a Simple Chatbot with OpenAI API', module: 'Module 1', dueDate: '2026-04-15', status: 'completed', score: 92, maxScore: 100 },
  { id: 2, title: 'Prompt Engineering Challenge: 10 Use Cases', module: 'Module 2', dueDate: '2026-04-25', status: 'completed', score: 88, maxScore: 100 },
  { id: 3, title: 'Implement RAG with LangChain', module: 'Module 3', dueDate: '2026-05-05', status: 'pending', score: null, maxScore: 100 },
  { id: 4, title: 'Fine-tuning Analysis Report', module: 'Module 4', dueDate: '2026-05-12', status: 'pending', score: null, maxScore: 100 },
  { id: 5, title: 'Capstone Project Proposal', module: 'Module 5', dueDate: '2026-05-20', status: 'overdue', score: null, maxScore: 100 },
];

const statusConfig = {
  completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, iconColor: 'text-green-500', label: 'Completed' },
  pending: { color: 'bg-blue-100 text-blue-700', icon: Clock, iconColor: 'text-blue-500', label: 'Pending' },
  overdue: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, iconColor: 'text-red-500', label: 'Overdue' },
};

export default function AssignmentsSection() {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? assignments : assignments.filter(a => a.status === filter);
  const completed = assignments.filter(a => a.status === 'completed').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Assignments</h2>
          <p className="text-muted-foreground text-sm mt-1">{completed}/{assignments.length} completed</p>
        </div>
        <div className="flex gap-2">
          {['all', 'completed', 'pending', 'overdue'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all ${filter === f ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25' : 'glass border border-black/10 text-muted-foreground hover:text-foreground'}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((a, i) => {
          const s = statusConfig[a.status];
          const StatusIcon = s.icon;
          const isOverdue = new Date(a.dueDate) < new Date() && a.status !== 'completed';
          return (
            <motion.div key={a.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
              whileHover={{ x: 4, transition: { duration: 0.2 } }}
              className="glass rounded-2xl border border-white/80 p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center hover:border-primary/20 transition-colors">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${a.status === 'completed' ? 'bg-green-100' : a.status === 'overdue' ? 'bg-red-100' : 'bg-blue-100'}`}>
                <StatusIcon className={`w-5 h-5 ${s.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-sm">{a.title}</h3>
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{a.module}</span>
                  <span className={`flex items-center gap-1 ${isOverdue && a.status !== 'completed' ? 'text-red-500 font-semibold' : ''}`}>
                    <Clock className="w-3 h-3" /> Due: {new Date(a.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {a.score !== null && (
                    <span className="font-semibold text-green-600">{a.score}/{a.maxScore} points</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.color}`}>{s.label}</span>
                {a.status === 'completed' ? (
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-xl text-xs font-semibold border-black/15 hover:bg-primary/10">
                    <Eye className="w-3.5 h-3.5" /> View
                  </Button>
                ) : (
                  <Button size="sm" className="gap-1.5 rounded-xl text-xs font-semibold bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90">
                    <Upload className="w-3.5 h-3.5" /> Submit
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}